/**
 * Delete Image API
 *
 * Deletes an image from S3 via the backend API
 *
 * **Backend Reference:**
 * - Repository: `images-backend`
 * - Function: `image`
 * - Method: DELETE
 * - Endpoint: VITE_APP_IMAGE_DELETE_URL (fallback: VITE_APP_IMAGE_UPLOAD_URL)
 *
 * **Request Query Parameters:**
 * ```
 * ?image_name=merchantName_elementID&image_type=fundraiser&nonprofitName=organizationName
 * ```
 *
 * **Response:**
 * - Success: Image deleted from S3 bucket
 * - Returns: Success status
 *
 * @param {Object} params - Delete parameters
 * @param {string} params.imageName - Image name (format: merchantName_elementID)
 * @param {string} params.imageType - Type of image ('fundraiser' | 'event' | 'logo')
 * @param {string} params.nonprofitName - Name of the nonprofit
 * @param {string} params.jwtToken - JWT authentication token
 *
 * @returns {Promise<{success: boolean, error?: string}>}
 *
 * @example
 * const result = await deleteImage({
 *   imageName: 'CharityOrg_abc123',
 *   imageType: 'fundraiser',
 *   nonprofitName: 'CharityOrg',
 *   jwtToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
 * });
 */

import { imagesDeleteInputSchema } from '@charitystack/org-engagement-contracts';
import { selectImagesClient } from '../../src/lib/flags/images';

export interface DeleteImageParams {
  organizationId?: unknown;
  imageName: string;
  imageType: 'fundraiser' | 'event' | 'logo';
  nonprofitName: string;
  jwtToken: string;
}

export interface DeleteImageResponse {
  success: boolean;
  error?: string;
}

export const deleteImage = async (params: DeleteImageParams): Promise<DeleteImageResponse> => {
  const { organizationId, imageName, imageType, nonprofitName, jwtToken } = params;

  // Validate required parameters
  if (!imageName || !imageType || !nonprofitName) {
    return {
      success: false,
      error: 'Missing required parameters',
    };
  }

  const parsedPayload = imagesDeleteInputSchema.safeParse({
    image_name: imageName,
    image_type: imageType,
    nonprofitName: nonprofitName,
  });
  if (!parsedPayload.success) return { success: false, error: 'Missing required parameters' };
  const deletePayload = parsedPayload.data;

  const queryParams = Object.entries(deletePayload)
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');

  const requestOptions = {
    method: 'DELETE',
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      Authorization: jwtToken,
      'x-cs-authorization': jwtToken,
    },
  };

  try {
    const client = selectImagesClient({ organizationId, authorization: jwtToken });
    if (client) {
      await client.deleteImage(deletePayload);
      return { success: true };
    }
    // Use VITE_APP_IMAGE_DELETE_URL if available, otherwise fallback to VITE_APP_IMAGE_UPLOAD_URL
    const url =
      import.meta.env.VITE_APP_IMAGE_DELETE_URL || import.meta.env.VITE_APP_IMAGE_UPLOAD_URL;

    const response = await fetch(`${url}?${queryParams}`, requestOptions);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || 'Failed to delete image',
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
